import React from 'react';
import { BookOpen, Package, Receipt, MessageCircle, Settings, HelpCircle, CheckCircle, AlertTriangle } from 'lucide-react';

export function VendorTutorial() {
  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-neutral-200 dark:border-neutral-800 p-8 shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <BookOpen className="w-6 h-6 text-neutral-900 dark:text-white" />
          <h2 className="text-2xl font-serif font-medium text-neutral-900 dark:text-white">Vendor Guide & Tutorial</h2>
        </div>
        <p className="text-neutral-600 dark:text-neutral-400">
          Welcome to the Cardston Market vendor dashboard! Here is a quick guide to help you manage your store, fulfill orders, and connect with your customers.
        </p>
      </div>

      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-900/50 rounded-2xl p-6">
        <h3 className="text-lg font-medium text-blue-900 dark:text-blue-100 mb-2 flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          Step 1: Get Connected for Payouts
        </h3>
        <p className="text-sm text-blue-800 dark:text-blue-200 mb-3">
          To receive funds for your sales, you MUST connect your payment account before you start selling. Please go to the <strong>Payment Configs</strong> tab right now and connect with Stripe. Once onboarded, payouts will be routed to your account automatically upon an order's completion. We charge a flat 5% platform fee on all sales.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <TutorialCard 
          icon={<Store />}
          title="2. Configure Your Storefront"
          description="Head over to Store Settings to update your store name, description, and pickup location. You can also customize your store's banner, colors, and social links to build your brand."
          tabName="Store Settings"
        />
        
        <TutorialCard 
          icon={<Package />}
          title="3. Add Products"
          description="Use the My Products tab to add your items. Make sure to define prices, stock levels, max order quantities, and clearly describe the item."
          tabName="My Products"
        />

        <TutorialCard 
          icon={<Receipt />}
          title="4. Process Orders"
          description="When customers buy your products, they appear in the My Orders tab. You can update order statuses (e.g., Pending, Ready for Pickup) so customers know when to arrive."
          tabName="My Orders"
        />

        <TutorialCard 
          icon={<MessageCircle />}
          title="5. Messages & Q&A"
          description="Customers may ask questions on your products (Product Q&A) or DM you directly (Messages). Timely responses build trust and boost sales."
          tabName="Messages / Product Q&A"
        />
      </div>
    </div>
  );
}

function Store() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinelinejoin="round" className="lucide lucide-store"><path d="m2 7 4.41-4.41A2 2 0 0 1 7.83 2h8.34a2 2 0 0 1 1.42.59L22 7"/><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><path d="M15 22v-4a2 2 0 0 0-2-2h-2a2 2 0 0 0-2 2v4"/><path d="M2 7h20"/><path d="M22 7v3a2 2 0 0 1-2 2v0a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 16 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 12 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 8 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 4 12v0a2 2 0 0 1-2-2V7"/></svg>
  )
}

function TutorialCard({ icon, title, description, tabName }: { icon: React.ReactNode, title: string, description: string, tabName: string }) {
  return (
    <div className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-center gap-4 mb-4">
        <div className="p-3 bg-neutral-100 dark:bg-neutral-800 text-neutral-900 dark:text-white rounded-lg">
          {icon}
        </div>
        <h3 className="text-lg font-medium text-neutral-900 dark:text-white">{title}</h3>
      </div>
      <p className="text-neutral-600 dark:text-neutral-400 text-sm mb-4 leading-relaxed">
        {description}
      </p>
      <div className="text-xs font-medium text-neutral-500 dark:text-neutral-500 uppercase tracking-wider">
        Look for: {tabName}
      </div>
    </div>
  );
}
